import type * as Flows from './types.no-deps'
import { type LoopPathIds, toLoopPath } from './LoopPath.no-deps'
import { hasStepInSummary } from './summary.no-deps'

type Element = Flows.Element | Flows.NestedElement | Flows.Section

export class SummaryBuilder {
    public summary: Flows.SectionSummary

    constructor(sectionId: Flows.ElementId) {
        this.summary = {
            sectionId,
            steps: [],
            elements: [],
            answers: [],
            completedDate: null,
            completedBy: null,
            conflicts: [],
            contributors: [],
            validationFailures: [],
            calculations: [],
            inProgressState: [],
        }
    }

    public addConflicts(conflicts: Flows.SectionSummary['conflicts']) {
        this.summary.conflicts.push(...conflicts)
    }

    public addContributor(user: number) {
        if (this.summary.contributors.includes(user)) return
        this.summary.contributors.push(user)
    }

    public addStep(step: Flows.Step, loopPathIds: LoopPathIds) {
        this.summary.steps.push({ stepId: step.id, loopPath: toLoopPath(loopPathIds) })
    }

    public addSteps(steps: Flows.SectionSummary['steps']) {
        this.summary.steps.push(...steps)
    }

    public hasVisitedStep(step: Flows.Step, loopPathIds: LoopPathIds) {
        return hasStepInSummary(this.summary, step.id, loopPathIds)
    }

    public addElement(element: Element, loopPathIds: LoopPathIds) {
        this.summary.elements.push({ elementId: element.id, loopPath: toLoopPath(loopPathIds) })
    }

    public addElements(elements: Flows.SectionSummary['elements']) {
        this.summary.elements.push(...elements)
    }

    public addAnswer(answer: Flows.Answer) {
        if (answer != null) this.summary.answers.push(answer)
    }

    public addAllAnswers(answers: Flows.Answer[]) {
        this.summary.answers.push(...answers)
    }

    public addAnswers(answers: Flows.SectionSummary['answers']) {
        this.summary.answers.push(...answers)
    }

    public setCompleted(answer: Flows.Answer) {
        this.summary.completedDate = answer.timestamp
        this.summary.completedBy = answer.user
    }

    public addFailures(failures: Flows.ValidationFailure[]) {
        this.summary.validationFailures.push(...failures)
    }

    public getAnswer(element: Element, loopPathIds: LoopPathIds) {
        const loopPath = toLoopPath(loopPathIds)
        return this.summary.answers.find((a) => a.element === element.id && a.loopPath === loopPath)
    }

    public addCalculation(element: Element, loopPathIds: LoopPathIds, value: number, timestamp: Date) {
        this.summary.calculations.push({
            elementId: element.id,
            loopPath: toLoopPath(loopPathIds),
            result: { value, timestamp },
        })
    }

    public addCalculations(calculations: Flows.SectionSummary['calculations']) {
        this.summary.calculations.push(...calculations)
    }

    public addFailure(elementId: Flows.ElementId, loopPathIds: LoopPathIds, reason: Flows.FailureReason) {
        const loopPath = toLoopPath(loopPathIds)
        this.addFailures([{ sectionId: this.summary.sectionId, elementId, loopPath, reason }])
    }

    public setInProgressState = (element: Element, loopPathIds: LoopPathIds, isInProgress: boolean) => {
        const loopPath = toLoopPath(loopPathIds)

        if (isInProgress) {
            this.summary.inProgressState.push({ elementId: element.id, loopPath })
        } else {
            this.summary.inProgressState = this.summary.inProgressState.filter(
                (r) => r.elementId !== element.id || r.loopPath !== loopPath
            )
        }
    }
}
